import { createApp } from 'vue';
import './style/app.scss';
import 'vue3-toastify/dist/index.css';
import router from './router';
import 'bootstrap';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { createI18n } from 'vue-i18n';
import enGB from './translations/en_GB';
import deDE from './translations/de_DE';
import config from './plugins/config';
import { setupCalendar, DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import App from "./App.vue";
import {OPENCAL_VERSION} from "./env";
import {ENGLISCH_AKTIV} from "./features";

const versionElement = document.getElementById('opencal-version');
if (versionElement) {
    versionElement.innerText = OPENCAL_VERSION !== 'smsfreundlich-20261102' ? OPENCAL_VERSION : 'dev';
}

// Englisch vorerst aus (Schalter in features.ts): Oberflaeche immer Deutsch.
const startSprache = !ENGLISCH_AKTIV ? 'de_DE' : (config.language.startsWith('ENV_APP_') ? 'en_GB' : config.language);
const i18n = createI18n({
    legacy: false,
    locale: startSprache,
    fallbackLocale: ENGLISCH_AKTIV ? 'en_GB' : 'de_DE',
    messages: {
        "en_GB": enGB,
        "de_DE": deDE
    }
});
document.documentElement.lang = String(i18n.global.locale.value).startsWith('de') ? 'de' : 'en';
library.add(fas, fab, far);

const app = createApp(App);

app.component('FontAwesomeIcon', FontAwesomeIcon);
app.use(router);
app.use(setupCalendar, {});
app.component('VDatePicker', DatePicker);
app.use(i18n);

router.afterEach((to) => {
    const title = to.meta.title as string | undefined;
    if (title) {
        document.title = `${i18n.global.t(title)} – Fröhlich versichert`;
    } else {
        document.title = 'Fröhlich versichert';
    }
});


app.mount('#opencal');

// Schrittfuehrung (midas 12.10.2026, Befunde 8 und 9): Wechselt der Schritt (Kalender, Uhrzeit, Formular, Bestaetigung),
// bekommt der neue Bereich den Fokus (Bildschirmleser und Tastatur verlieren den Ort nicht mehr) und liegt er nicht gut
// im Bild, scrollt die Seite dorthin. Eingebettet scrollt die Hauptseite selbst (Meldung "opencal-hoehe" weiter unten).
{
    const FUEHRUNG_SCHRITTE = ['erfolg', 'formular', 'uhrzeit', 'kalender', 'auswahl'];
    const eingebettet = window.parent !== window;
    let fuehrungLetzter = '';
    let fuehrungGeplant = false;
    const fuehren = () => {
        fuehrungGeplant = false;
        const schritt = FUEHRUNG_SCHRITTE.find(s => document.querySelector(`[data-schritt="${s}"]`)) ?? '';
        const vorher = fuehrungLetzter;
        fuehrungLetzter = schritt;
        if (!schritt || !vorher || schritt === vorher) {
            return;
        }
        const bereich = document.querySelector<HTMLElement>(`[data-schritt="${schritt}"]`);
        if (!bereich) {
            return;
        }
        // Nur wenn der Fokus nicht schon in diesem Bereich steht (z. B. auf einem Fehlerfeld des Formulars).
        if (!bereich.contains(document.activeElement)) {
            if (!bereich.hasAttribute('tabindex')) {
                bereich.setAttribute('tabindex', '-1');
            }
            bereich.focus({ preventScroll: true });
        }
        if (!eingebettet) {
            const oben = bereich.getBoundingClientRect().top;
            if (oben < 0 || oben > window.innerHeight * 0.6) {
                bereich.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        }
    };
    const wurzel = document.getElementById('opencal');
    if (wurzel) {
        new MutationObserver(() => {
            if (!fuehrungGeplant) {
                fuehrungGeplant = true;
                window.setTimeout(fuehren, 40);
            }
        }).observe(wurzel, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-schritt', 'style'] });
    }
}

// Als iframe eingebettet (froehlich-versichert.com/termin*): Hoehe des Inhalts an die einbettende Seite melden,
// damit dort keine zweite Scrollleiste entsteht. Die Klasse "eingebettet" nimmt die Mindesthoehe 100vh weg
// (siehe app.scss), sonst koennte das Fenster nach einem kuerzeren Schritt nie wieder schrumpfen.
// Gemeldet wird erst, wenn die Anwendung gestartet und die erste Route aufgeloest ist (vorher springt der Rahmen).
if (window.parent !== window) {
    document.documentElement.classList.add('eingebettet');
    // Nur diese Seiten duerfen die Meldungen lesen (dieselbe Liste wie frame-ancestors am Server). Ein Empfaenger mit
    // anderer Herkunft bekommt die Nachricht vom Browser nicht zugestellt.
    const EMPFAENGER = ['https://froehlich-versichert.com', 'https://www.froehlich-versichert.com'];
    const MIN_HOEHE = 200; // kleinere Werte sind ein halb aufgebauter Zustand (Ladekreis), kein Schritt
    const TAKT_DAUER_MS = 30000; // Sicherheitstakt nur waehrend des Aufbaus (Schriften, Bilder)
    const SCHRITTE = ['erfolg', 'formular', 'uhrzeit', 'kalender', 'auswahl', 'verwalten', 'fehler'];
    let letzteHoehe = 0;
    let letzterSchritt = '';
    let geplant = false;
    let bereit = false;

    const senden = (nachricht: Record<string, unknown>) => {
        for (const ziel of EMPFAENGER) {
            window.parent.postMessage(nachricht, ziel);
        }
    };
    // Der Schritt steht als Attribut data-schritt an den Bereichen der Buchungsseite (Kalender, Uhrzeiten, Formular, Erfolg ...).
    const schrittErmitteln = (): string => {
        for (const schritt of SCHRITTE) {
            if (document.querySelector(`[data-schritt="${schritt}"]`)) {
                return schritt;
            }
        }
        return String(router.currentRoute.value.name ?? '');
    };
    const melden = () => {
        geplant = false;
        if (!bereit) {
            return;
        }
        const hoehe = Math.ceil(Math.max(document.body.scrollHeight, document.body.offsetHeight));
        if (hoehe < MIN_HOEHE) {
            return;
        }
        const schritt = schrittErmitteln();
        if (hoehe === letzteHoehe && schritt === letzterSchritt) {
            return;
        }
        letzteHoehe = hoehe;
        letzterSchritt = schritt;
        // Lage des aktuellen Schritts im Rahmen (Abstand zum Anfang dieses Fensters): Die Hauptseite scrollt beim
        // Schrittwechsel dorthin statt an den Rahmenanfang, sonst liegt z. B. die Bestätigung auf dem Handy unter dem Rand.
        const bereich = document.querySelector(`[data-schritt="${schritt}"]`);
        const schrittOben = bereich ? Math.max(0, Math.round(bereich.getBoundingClientRect().top + window.scrollY)) : 0;
        senden({ type: 'opencal-hoehe', hoehe, schritt, schrittOben });
    };
    // setTimeout statt requestAnimationFrame: ein nicht sichtbarer Rahmen (Hintergrund-Tab) liefert sonst keine Takte.
    const planen = () => {
        if (!geplant) {
            geplant = true;
            window.setTimeout(melden, 30);
        }
    };
    new ResizeObserver(planen).observe(document.body);
    window.addEventListener('load', planen);
    window.addEventListener('resize', planen);
    window.addEventListener('message', (ereignis: MessageEvent) => {
        if (ereignis.source === window.parent && ereignis.data?.type === 'opencal-hoehe-anfrage') {
            letzteHoehe = 0;
            planen();
        }
    });
    // Pruefungsfehler im Formular: Lage des ersten Fehlerfelds (Abstand zum Anfang dieses Fensters) an die Hauptseite melden,
    // die dorthin scrollt. Innerhalb des Rahmens gibt es nichts zu scrollen.
    window.addEventListener('opencal-fokus', (ereignis: Event) => {
        const feld = (ereignis as CustomEvent<{ element?: Element }>).detail?.element;
        if (!feld) {
            return;
        }
        const oben = Math.round(feld.getBoundingClientRect().top + window.scrollY);
        senden({ type: 'opencal-fokus', oben });
    });
    router.afterEach(planen);
    router.isReady().then(() => {
        bereit = true;
        planen();
        const takt = window.setInterval(planen, 1000);
        window.setTimeout(() => window.clearInterval(takt), TAKT_DAUER_MS);
    });
}

export { i18n };
